import React, { useMemo } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';

import type { KpiSummaryResponse } from '../../services/KpiSummaryService';
import type { InvertedLocalize } from '../../utils/localization';

import { useKpiSummary } from '../../hooks/use-kpisummary';
import { useInvertedLocalize } from '../../utils/localization';

import KpiTile from './KpiTile/KpiTile';
import invertedMessages, { type KpiTilesMessage } from './messages';

import './KpiTiles.css';

// types for KPI data
export interface KpiData {
  id: string;
  title: string;
  value: string | number;
  subtitle: string;
  // TODO: use change/delta in future mvp
  // change: {
  //   value: string;
  //   type: 'positive' | 'negative';
  // };
  icon: IconKind;
}

// Single source of truth for static KPI tile metadata
const KPI_TILE_METADATA = [
  {
    id: 'pending-recommendations',
    title: 'Pending Recommendations' as const,
    icon: 'Time' as const,
    apiKey: 'pendingRecommendations' as const,
  },
  {
    id: 'active-recommended-rules',
    title: 'Active Recommended Rules' as const,
    icon: 'QuickAction' as const,
    apiKey: 'assignedAccess' as const,
  },
  {
    id: 'total-active-rules',
    title: 'Total Active Rules' as const,
    icon: 'Group' as const,
    apiKey: 'activeRules' as const,
  },
  {
    id: 'workforce-time-saved',
    title: 'Workforce Time Saved' as const,
    icon: 'RequestApproval' as const,
    apiKey: 'workforceTimeSaved' as const,
  },
] as const;

// type for localization function with placeholders
type LocalizeFn = InvertedLocalize<KpiTilesMessage>;

/**
 * Helper function to generate workforce time saved subtitle
 */
export function getWorkforceTimeSavedSubtitle(
  workforceData: { label?: string | null },
  l: LocalizeFn
): string {
  // no need to localize N/A below as it's a fallback that will be isNaN
  const value = workforceData.label ?? 'N/A';
  const numValue = Number(value);

  // if not a valid number OR if it's 0, show N/A string
  if (Number.isNaN(numValue) || numValue === 0) {
    return l('N/A hours of total workforce time saved');
  }

  return l('{value} hours of total workforce time saved', { value });
}

/**
 * Helper function to generate pending recommendations subtitle
 */
export function getPendingRecsSubtitle(
  pendingData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(pendingData.label) || 0;

  return l('Covers {count} assigned access', { count });
}

/**
 * Helper function to generate total active rules subtitle
 */
export function getTotalActiveRulesSubtitle(
  activeRulesData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(activeRulesData.label) || 0;

  return l('{count} Assigned access from these Rules', { count });
}

/**
 * Helper function to generate assigned access subtitle
 */
export function getAssignedAccessSubtitle(
  assignedAccessData: { label?: string | null },
  l: LocalizeFn
): string {
  const count = Number(assignedAccessData.label) || 0;

  return l('{count} Assigned access from these Rules', { count });
}

/**
 * Transform API response to KpiData format using metadata configuration
 */
export function transformKpiData(
  apiData: KpiSummaryResponse,
  l: LocalizeFn
): KpiData[] {
  const { summary } = apiData;

  return KPI_TILE_METADATA.map((metadata) => {
    let subtitle: string;

    if (metadata.apiKey === 'workforceTimeSaved') {
      subtitle = getWorkforceTimeSavedSubtitle(summary[metadata.apiKey], l);
    } else if (metadata.apiKey === 'pendingRecommendations') {
      subtitle = getPendingRecsSubtitle(summary[metadata.apiKey], l);
    } else if (metadata.apiKey === 'activeRules') {
      subtitle = getTotalActiveRulesSubtitle(summary[metadata.apiKey], l);
    } else {
      subtitle = getAssignedAccessSubtitle(summary[metadata.apiKey], l);
    }

    return {
      ...metadata,
      title: l(metadata.title),
      value: summary[metadata.apiKey].count,
      subtitle,
      change: {
        value: summary[metadata.apiKey].delta, // Delta included for future use
        type: 'positive',
      },
    };
  });
}

function KpiTiles() {
  const l = useInvertedLocalize(invertedMessages);

  const { data: apiData, isLoading, error } = useKpiSummary();

  // Determine which tiles to render based on loading state and data availability
  const tilesToRender = useMemo(() => {
    if (isLoading) {
      // Show skeleton tiles with localized titles
      return KPI_TILE_METADATA.map((metadata) => ({
        ...metadata,
        title: l(metadata.title),
      }));
    }

    // Handle all error cases here
    if (error) {
      return null;
    }

    if (!apiData?.summary) {
      return []; // empty state for no data
    }

    return transformKpiData(apiData, l);
  }, [apiData, isLoading, error, l]);

  // handle error state
  if (error) {
    Logger.error('KPI tiles API error', error);

    return (
      <Box tag='section' className='Rules-KpiTiles Rules-KpiTiles--error'>
        <div>
          <Icon
            kind='AlertWarningFilled'
            size='xLarge'
            color='--color-background-warning-bold'
          />
        </div>
        <Typography
          kind='body2-bold'
          color='--color-foreground-neutral-bold'
          className='Dashboard-filteredZeroRulesMessage'>
          {l('Unable to load KPI data')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box tag='section' className='Rules-KpiTiles'>
      {tilesToRender?.map((tile) => (
        <KpiTile key={tile.id} data={tile} isLoading={isLoading} />
      ))}
    </Box>
  );
}

export default KpiTiles;
